/**
 * Subscription Checkout Modal Component
 * Handles the checkout flow for subscription purchases
 * Integrates with Stripe payment gateway
 * Uses primary color from theme palette
 * 
 * Platform Support:
 * - Web: Uses Stripe Elements with PaymentElement
 * - Native: Uses Stripe Payment Sheet
 */

import React, {useMemo, useState, useCallback} from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  ScrollView,
  ViewStyle,
  TextStyle,
  ActivityIndicator,
  Alert,
  Platform,
  Linking,
} from 'react-native';
import {useTheme} from '~contexts/ThemeContext';
import {spacing, borderRadius} from '~constants/design';
import {
  SubscriptionPlanDetails,
  SubscriptionType,
  SubscriptionPlan,
} from '~types/subscription/subscription.types';
import ModernButton from '~components/ModernButton/ModernButton';
import {useStripe} from '@stripe/stripe-react-native';
import {useCreateSubscription} from '~hooks/subscription/useCreateSubscription';
import {Notifier, NotifierComponents} from 'react-native-notifier';
import { STRIPE_PUBLISH_KEY } from '@env';
import SubscriptionCheckoutModalWeb from './SubscriptionCheckoutModalWeb';

interface SubscriptionCheckoutModalProps {
  isVisible: boolean;
  plan: SubscriptionPlanDetails;
  subscriptionType: SubscriptionType;
  onClose: () => void;
  onSuccess: (planId: SubscriptionPlan, type: SubscriptionType) => void;
}

const SubscriptionCheckoutModal: React.FC<SubscriptionCheckoutModalProps> = ({
  isVisible,
  plan,
  subscriptionType,
  onClose,
  onSuccess,
}) => {
  // Delegate to web version on web platform
  if (Platform.OS === 'web') {
    return (
      <SubscriptionCheckoutModalWeb
        isVisible={isVisible}
        plan={plan}
        subscriptionType={subscriptionType}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    );
  }

  // Native implementation below
  return (
    <SubscriptionCheckoutModalNative
      isVisible={isVisible}
      plan={plan}
      subscriptionType={subscriptionType}
      onClose={onClose}
      onSuccess={onSuccess}
    />
  );
};

/**
 * Native version of Subscription Checkout Modal
 * Uses Stripe Payment Sheet for iOS/Android
 */
const SubscriptionCheckoutModalNative: React.FC<SubscriptionCheckoutModalProps> = ({
  isVisible,
  plan,
  subscriptionType,
  onClose,
  onSuccess,
}) => {
  const {colors, themeMode} = useTheme();
  const [isProcessing, setIsProcessing] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const {initPaymentSheet, presentPaymentSheet} = useStripe();
  const {mutateAsync: createSubscription} = useCreateSubscription();

  const amount =
    subscriptionType === SubscriptionType.MONTHLY
      ? plan.pricing.monthly
      : plan.pricing.oneTimePass;

  const styles = useMemo(() => {
    const modalOverlayStyle: ViewStyle = {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.5)',
      justifyContent: 'flex-end',
    };

    const modalContainerStyle: ViewStyle = {
      backgroundColor: colors.background,
      borderTopLeftRadius: borderRadius['3xl'],
      borderTopRightRadius: borderRadius['3xl'],
      paddingTop: spacing.lg,
      maxHeight: '90%',
    };

    const handleStyle: ViewStyle = {
      width: 40,
      height: 4,
      backgroundColor: colors.border,
      borderRadius: borderRadius.full,
      alignSelf: 'center',
      marginBottom: spacing.lg,
    };

    const scrollContentStyle: ViewStyle = {
      padding: spacing.lg,
    };

    const headerStyle: ViewStyle = {
      marginBottom: spacing.lg,
    };

    const planNameStyle: TextStyle = {
      fontSize: 22,
      fontWeight: '800',
      color: colors.text,
      marginBottom: spacing.xs,
    };

    const planTypeStyle: TextStyle = {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: spacing.lg,
    };

    const summaryBoxStyle: ViewStyle = {
      backgroundColor: colors.primary + '15',
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.lg,
      borderLeftWidth: 4,
      borderLeftColor: colors.primary,
    };

    const summaryRowStyle: ViewStyle = {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: spacing.sm,
    };

    const summaryLabelStyle: TextStyle = {
      fontSize: 14,
      color: colors.textSecondary,
      fontWeight: '500',
    };

    const summaryValueStyle: TextStyle = {
      fontSize: 16,
      fontWeight: '700',
      color: colors.text,
    };

    const totalRowStyle: ViewStyle = {
      ...summaryRowStyle,
      borderTopWidth: 1,
      borderTopColor: colors.primary + '30',
      marginTop: spacing.sm,
      paddingTop: spacing.sm,
    };

    const totalValueStyle: TextStyle = {
      fontSize: 18,
      fontWeight: '800',
      color: colors.primary,
    };

    const termsContainerStyle: ViewStyle = {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: spacing.lg,
      paddingHorizontal: spacing.sm,
      paddingVertical: spacing.md,
      backgroundColor: colors.gray50,
      borderRadius: borderRadius.lg,
    };

    const termsCheckboxStyle: ViewStyle = {
      width: 24,
      height: 24,
      borderRadius: 6,
      borderWidth: 2,
      borderColor: agreedToTerms ? colors.primary : colors.border,
      backgroundColor: agreedToTerms ? colors.primary : 'transparent',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: spacing.sm,
      marginTop: 2,
    };

    const termsTextStyle: TextStyle = {
      fontSize: 12,
      color: colors.textSecondary,
      flex: 1,
      lineHeight: 18,
    };

    const termsLinkStyle: TextStyle = {
      color: colors.primary,
      fontWeight: '600',
      textDecorationLine: 'underline',
    };

    const buttonContainerStyle: ViewStyle = {
      flexDirection: 'row',
      gap: spacing.md,
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.lg,
    };

    const primaryButtonStyle: ViewStyle = {
      flex: 1,
      backgroundColor: colors.primary,
      paddingVertical: spacing.lg,
      borderRadius: borderRadius.lg,
      alignItems: 'center',
      justifyContent: 'center',
    };

    const secondaryButtonStyle: ViewStyle = {
      flex: 1,
      backgroundColor: colors.gray50,
      paddingVertical: spacing.lg,
      borderRadius: borderRadius.lg,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: colors.border,
    };

    const buttonTextStyle: TextStyle = {
      fontSize: 15,
      fontWeight: '700',
      letterSpacing: 0.5,
    };

    const infoBannerStyle: ViewStyle = {
      backgroundColor: colors.background,
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.lg,
      borderRadius: borderRadius.lg,
      marginBottom: spacing.lg,
      borderWidth: 1,
      borderColor: colors.border + '30',
    };

    const infoBannerTextStyle: TextStyle = {
      fontSize: 12,
      color: colors.textSecondary,
      lineHeight: 16,
      textAlign: 'center',
    };

    return {
      modalOverlayStyle,
      modalContainerStyle,
      handleStyle,
      scrollContentStyle,
      headerStyle,
      planNameStyle,
      planTypeStyle,
      summaryBoxStyle,
      summaryRowStyle,
      summaryLabelStyle,
      summaryValueStyle,
      totalRowStyle,
      totalValueStyle,
      termsContainerStyle,
      termsCheckboxStyle,
      termsTextStyle,
      termsLinkStyle,
      buttonContainerStyle,
      primaryButtonStyle,
      secondaryButtonStyle,
      buttonTextStyle,
      infoBannerStyle,
      infoBannerTextStyle,
    };
  }, [colors, themeMode, plan, agreedToTerms]);

  const handleCheckout = useCallback(async () => {
    if (!agreedToTerms) {
      return;
    }

    setIsProcessing(true);

    try {
      // Step 1: Create subscription via API
      console.log('🔍 handleCheckout: Creating subscription for plan:', plan.id);

      const subscriptionData = await createSubscription({
        plan_id: plan.id,
      });

      console.log('✅ handleCheckout: Subscription created:', STRIPE_PUBLISH_KEY, subscriptionData);

      // Step 2: Initialize Stripe payment sheet
      const merchantDisplayName = 'Pyxi';
      const initError = await initPaymentSheetWithSecret(subscriptionData.client_secret, merchantDisplayName);

      if (initError) {
        throw new Error(initError);
      }

      
      // Step 3: Present payment sheet to user
      const presentError = await presentPaymentSheetWrapper(subscriptionData);

      if (presentError) {
        throw new Error(presentError);
      }

      // Step 4: Call success callback
      console.log('✅ handleCheckout: Payment successful');
      onSuccess(plan.id, subscriptionType);

      // Show success notification
      Notifier.showNotification({
        title: 'Subscription Activated!',
        description: `You've successfully subscribed to ${plan.name}.`,
        Component: NotifierComponents.Alert,
        componentProps: {
          alertType: 'success',
        },
      });

      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error: any) {
      console.error('❌ handleCheckout: Payment error:', error);
      setIsProcessing(false);

      const errorMessage = error.message || 'Failed to process subscription. Please try again.';

      // Show error notification
      if (Platform.OS != 'web') {
      Notifier.showNotification({
        title: 'Payment Failed',
        description: errorMessage,
        Component: NotifierComponents.Alert,
        componentProps: {
          alertType: 'error',
        },
      });
      }
      Alert.alert('Payment Error', errorMessage, [
        {
          text: 'OK',
          onPress: () => {
            setIsProcessing(false);
          },
        },
      ]);
    }
  }, [agreedToTerms, plan.id, plan.name, subscriptionType, onSuccess, onClose, createSubscription, initPaymentSheet, presentPaymentSheet]);

  // Helper function to initialize payment sheet
  const initPaymentSheetWithSecret = useCallback(
    async (clientSecret: string, merchantName: string): Promise<string | null> => {
      try {
        console.log('🔍 initPaymentSheetWithSecret: Initializing payment sheet');
        const {error} = await initPaymentSheet({
          merchantDisplayName: merchantName,
          paymentIntentClientSecret: clientSecret,
      returnURL: 'https://partner.pyxi.ai/payment-return',
        });

        if (error) {
          console.error('❌ initPaymentSheetWithSecret: Error', error.message);
          return error.message;
        }

        console.log('✅ initPaymentSheetWithSecret: Payment sheet initialized successfully');
        return null;
      } catch (error: any) {
        console.error('❌ initPaymentSheetWithSecret: Exception', error);
        return error.message || 'Failed to initialize payment sheet';
      }
    },
    [initPaymentSheet],
  );

  // Helper function to present payment sheet
  const presentPaymentSheetWrapper = useCallback(
    async (subscriptionData: any): Promise<string | null> => {
      try {
        console.log('🔍 presentPaymentSheetWrapper: Presenting payment sheet');
        const {error} = await presentPaymentSheet();

        if (error) {
          console.error('❌ presentPaymentSheetWrapper: Error', error.message);
          return error.message;
        }

        console.log('✅ presentPaymentSheetWrapper: Payment sheet completed successfully');
        return null;
      } catch (error: any) {
        console.error('❌ presentPaymentSheetWrapper: Exception', error);
        return error.message || 'Failed to present payment sheet';
      }
    },
    [presentPaymentSheet],
  );

  const billingPeriod =
    subscriptionType === SubscriptionType.MONTHLY ? 'per month' : 'one-time';

  return (
    <Modal
      visible={isVisible}
      transparent
      animationType="slide"
      onRequestClose={onClose}>
      <View style={styles.modalOverlayStyle}>
        <View
          style={styles.modalContainerStyle}>
          <View style={styles.handleStyle} />

          <ScrollView
            contentContainerStyle={styles.scrollContentStyle}
            showsVerticalScrollIndicator={false}>
            {/* Header */}
            <View style={styles.headerStyle}>
              <Text style={styles.planNameStyle}>{plan.name}</Text>
              <Text style={styles.planTypeStyle}>
                {subscriptionType === SubscriptionType.MONTHLY
                  ? 'Monthly Subscription'
                  : 'One-Time Pass'}
              </Text>
            </View>

            {/* Order Summary */}
            <View style={styles.summaryBoxStyle}>
              <View style={styles.summaryRowStyle}>
                <Text style={styles.summaryLabelStyle}>Plan Name</Text>
                <Text style={styles.summaryValueStyle}>{plan.shortName || plan.name}</Text>
              </View>

              <View style={styles.summaryRowStyle}>
                <Text style={styles.summaryLabelStyle}>Billing Cycle</Text>
                <Text style={styles.summaryValueStyle}>{billingPeriod}</Text>
              </View>

              <View style={styles.summaryRowStyle}>
                <Text style={styles.summaryLabelStyle}>Subtotal</Text>
                <Text style={styles.summaryValueStyle}>
                  £{amount?.toFixed(2)}
                </Text>
              </View>

              <View style={styles.totalRowStyle}>
                <Text style={{...styles.summaryLabelStyle, fontSize: 16, fontWeight: '700'}}>
                  Total Amount
                </Text>
                <Text style={styles.totalValueStyle}>
                  £{amount?.toFixed(2)}
                </Text>
              </View>
            </View>

            {/* What's Included */}
            <View style={styles.infoBannerStyle}>
              <Text style={{...styles.infoBannerTextStyle, marginBottom: spacing.sm, fontWeight: '600', color: colors.text, textAlign: 'left'}}>
                What's Included:
              </Text>
              {plan.features
                .filter(f => f.included)
                .map((feature, idx) => (
                  <Text
                    key={feature.id}
                    style={{
                      ...styles.infoBannerTextStyle,
                      color: colors.text,
                      textAlign: 'left',
                      marginBottom: idx < plan.features.filter(f => f.included).length - 1 ? spacing.xs : 0,
                    }}>
                    • {feature.name}
                  </Text>
                ))}
            </View>

            {/* Terms & Conditions */}
            <View style={styles.termsContainerStyle}>
              <TouchableOpacity
                style={styles.termsCheckboxStyle}
                onPress={() => setAgreedToTerms(!agreedToTerms)}
                activeOpacity={0.7}>
                {agreedToTerms && (
                  <Text style={{fontSize: 14, fontWeight: '700', color: '#FFFFFF'}}>
                    ✓
                  </Text>
                )}
              </TouchableOpacity>
              <Text style={styles.termsTextStyle}>
                I agree to the Subscription{' '}
                <Text
                  style={styles.termsLinkStyle}
                  onPress={() => Linking.openURL('https://www.pyxi.ai/terms-and-conditions')}>
                  Terms & Conditions
                </Text>{' '}
                and{' '}
                <Text
                  style={styles.termsLinkStyle}
                  onPress={() => Linking.openURL('https://www.pyxi.ai/privacy-policy')}>
                  Privacy Policy
                </Text>
                . My subscription will auto-renew on the same date each{' '}
                {subscriptionType === SubscriptionType.MONTHLY ? 'month' : 'year'}.
              </Text>
            </View>

            {/* Important Info */}
            <View
              style={{
                backgroundColor: colors.gray50,
                padding: spacing.md,
                borderRadius: borderRadius.lg,
                marginBottom: spacing.lg,
              }}>
              <Text
                style={{
                  fontSize: 12,
                  color: colors.black,
                  lineHeight: 16,
                }}>
                💳 Your payment is secure and encrypted.{'\n'}You can cancel anytime from your account settings.
              </Text>
            </View>
          </ScrollView>

          {/* Action Buttons */}
          <View style={styles.buttonContainerStyle}>
            <TouchableOpacity
              style={styles.secondaryButtonStyle}
              onPress={onClose}
              disabled={isProcessing}
              activeOpacity={0.8}>
              <Text style={{...styles.buttonTextStyle, color: colors.text}}>
                Cancel
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.primaryButtonStyle,
                !agreedToTerms && {opacity: 0.6},
              ]}
              onPress={handleCheckout}
              disabled={!agreedToTerms || isProcessing}
              activeOpacity={0.85}>
              {isProcessing ? (
                <ActivityIndicator color="#FFFFFF" size="small" />
              ) : (
                <Text style={{...styles.buttonTextStyle, color: '#FFFFFF'}}>
                  Pay £{amount?.toFixed(2)}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default SubscriptionCheckoutModal;
